import type { AxiosInstance } from 'axios';

import { throwApiError } from 'src/api/utils';

type TGOStorageObject<T extends object = object> = {
  id: number;
  createdAt: number;
  data: { ownerUserId: number; settings: T };
};

export class UserSettingsServiceApi {
  private readonly userId: string;
  private readonly agent: AxiosInstance;

  constructor(goStorageAgent: AxiosInstance, userId: string) {
    this.agent = goStorageAgent;
    this.userId = userId;
  }

  async createSettings<T extends object = object>(
    objectName: string,
    initialSettings: T = {} as T,
  ): Promise<{ settings: T; settingsId: number }> {
    try {
      const { data } = await this.agent.post<TGOStorageObject<T>>(`object/new/${objectName}`, {
        ownerUserId: this.userId,
        settings: initialSettings,
      });

      return {
        settings: initialSettings,
        settingsId: data.id,
      };
    } catch (e) {
      throw throwApiError(e);
    }
  }

  async updateSettings<T extends object = object>(
    settings: T,
    objectName: string,
    settingsId: number,
  ): Promise<{ settings: T; settingsId: number }> {
    try {
      const { data } = await this.agent.patch<{ settings: T }>(`object/${objectName}/${settingsId}`, {
        settings,
      });

      const { settings: newSettings } = data;

      return {
        settings: newSettings,
        settingsId: settingsId,
      };
    } catch (e) {
      throwApiError(e);
    }
  }

  async loadSettings<T extends object = object>(
    objectName: string,
  ): Promise<{ settings: T; settingsId: number } | null> {
    try {
      const { data } = await this.agent.get<TGOStorageObject<T>[]>(`object/list/${objectName}`, {
        params: {
          ownerUserId: this.userId,
        },
      });

      const settingsData = data.at(0);

      if (!settingsData) {
        return null;
      }

      return {
        settings: settingsData.data.settings,
        settingsId: settingsData.id,
      };
    } catch (e) {
      throwApiError(e);
    }
  }

  async loadWellTableSettings<T extends object = object>(): Promise<T | null> {
    try {
      const { data } = await this.agent.get<T>('userSettings/wellsTableSettings');

      if (!data) {
        return null;
      }

      return data;
    } catch (e) {
      throwApiError(e);
    }
  }

  async updateWellTableSettings<T extends object = object>(settings: T): Promise<T | null> {
    try {
      await this.agent.patch<T>('userSettings/wellsTableSettings', settings);
      const data = await this.loadWellTableSettings<T>();

      return data;
    } catch (e) {
      throwApiError(e);
    }
  }

  async createWellTableSettings<T extends object = object>(settings: Partial<T> = {} as Partial<T>): Promise<T | null> {
    try {
      await this.agent.post<T>('userSettings/wellsTableSettings/new', settings);
      const data = await this.loadWellTableSettings<T>();

      return data;
    } catch (e) {
      throwApiError(e);
    }
  }
}
